<template>
  <svg :x="x" :y="y" :width="`${widthInInches}in`" :height="`${heightInInches}in`" xmlns="http://www.w3.org/2000/svg" :index="matIndex" :key="matKey">
    <defs>
      <mask :id="`${uuid}-mat-mask-${matIndex}`">
        <rect width="100%" height="100%" fill="white"/>
        <rect
          v-for="artworkOpening in artworkOpenings"
          :key="artworkOpening.key"
          :x="artworkOpening.x"
          :y="artworkOpening.y"
          :width="artworkOpening.width"
          :height="artworkOpening.height"
          fill="black"
        />
      </mask>

      <pattern :x="surfaceTexturePatternOrigin.x" :y="surfaceTexturePatternOrigin.y" v-if="surfaceTexture" :id="`${uuid}-mat-tile-pattern-${matIndex}`" patternUnits="userSpaceOnUse" :width="surfaceTexture.widthInInches" :height="surfaceTexture.heightInInches">
        <image :href="surfaceTexture.url" :width="surfaceTexture.widthInInches" :height="surfaceTexture.heightInInches" preserveAspectRatio="xMaxYMin" />
      </pattern>
    </defs>
    <rect
      :width="matWidth"
      :height="matHeight"
      :fill="matColor"
      :mask="`url(#${uuid}-mat-mask-${matIndex})`"
    />
    <image v-if="matImage" :href="matImage" :width="matWidth" :height="matHeight"/>
    <template v-else>
      <rect
        v-for="artworkOpening in artworkOpenings"
        :key="artworkOpening.key"
        :x="artworkOpening.bevelX"
        :y="artworkOpening.bevelY"
        :width="artworkOpening.bevelWidth"
        :height="artworkOpening.bevelHeight"
        :stroke="coreColor"
        :stroke-width="bevelWidth"
        fill="none"
      />
    </template>
    <RenderSvgMatCaption
      v-if="matCaption"
      :frameSpec="frameSpec"
      :captionX="captionX"
      :captionY="captionY"
      :captionWidth="captionWidth"
    />
    <rect
      v-if="hasPlaque"
      :stroke="coreColor"
      :stroke-width="bevelWidth"
      :x="(plaqueX) - bevelWidth / 2"
      :y="(plaqueY) - bevelWidth / 2"
      :width="plaqueWidth + bevelWidth"
      :height="plaqueHeight + bevelWidth"
      fill="none"
    />
    <RenderSvgPlaque
      v-if="hasPlaque"
      :frameSpec="frameSpec"
      :plaqueY="plaqueY"
      :plaqueX="plaqueX"
      :plaqueWidth="plaqueWidth"
      :plaqueHeight="plaqueHeight"
    />  
  </svg>
</template>

<script>
import uuid from 'uuid/v4';
import { primaryMatColors, defaultMatThickness, matCaption as matCaptionValues, uploadedArtworkMatOverlap } from "../../hardCodedValues";
import RenderSvgMatCaption from './RenderSvgMatCaption.vue';
import RenderSvgPlaque from './RenderSvgPlaque.vue';

export default {
  components: {
    RenderSvgMatCaption,
    RenderSvgPlaque
  },
  props: {
    frameSpec: {
      type: Object,
      required: true
    },
    x: {
      type: Number,
      default: 0
    },
    y: {
      type: Number,
      default: 0
    },
    mat: {
      type: Object,
      required: true
    },
    matIndex: {
      type: Number,
      required: true
    },
  },
  computed: {
    fullMat () {
      return primaryMatColors.find(mat => mat.name === this.mat.name);
    },
    uuid () {
      return uuid();
    },
    matKey() {
      return `${this.mat.name}-${this.matIndex}`;
    },
    artwork () {
      return this.frameSpec.getArtwork(0);
    },
    artworks () {
      return this.frameSpec.artworks;
    },
    invertedMats () {
      return this.frameSpec.mats.slice().reverse();
    },
    totalsOfAllMatReveals() {
      return {
        topInInches: this.invertedMats.reduce((acc, mat) => acc + (mat.reveal && mat.reveal.topInInches), 0),
        rightInInches: this.invertedMats.reduce((acc, mat) => acc + (mat.reveal && mat.reveal.rightInInches), 0),
        bottomInInches: this.invertedMats.reduce((acc, mat) => acc + (mat.reveal && mat.reveal.bottomInInches), 0),
        leftInInches: this.invertedMats.reduce((acc, mat) => acc + (mat.reveal && mat.reveal.leftInInches), 0)
      };
    },
    totalsOfAllMatRevealsBelowThisMat () {
      return {
        topInInches: this.invertedMats.slice(0, this.matIndex).reduce((acc, mat) => acc + (mat.reveal && mat.reveal.topInInches), 0),
        rightInInches: this.invertedMats.slice(0, this.matIndex).reduce((acc, mat) => acc + (mat.reveal && mat.reveal.rightInInches), 0),
        bottomInInches: this.invertedMats.slice(0, this.matIndex).reduce((acc, mat) => acc + (mat.reveal && mat.reveal.bottomInInches), 0),
        leftInInches: this.invertedMats.slice(0, this.matIndex).reduce((acc, mat) => acc + (mat.reveal && mat.reveal.leftInInches), 0)
      };
    },
    totalsOfAllMatRevealsThisMatAndAbove () {
      const length = this.invertedMats.length;
      return {
        topInInches: this.invertedMats.slice(this.matIndex, length).reduce((acc, mat) => acc + (mat.reveal && mat.reveal.topInInches), 0),
        rightInInches: this.invertedMats.slice(this.matIndex, length).reduce((acc, mat) => acc + (mat.reveal && mat.reveal.rightInInches), 0),
        bottomInInches: this.invertedMats.slice(this.matIndex, length).reduce((acc, mat) => acc + (mat.reveal && mat.reveal.bottomInInches), 0),
        leftInInches: this.invertedMats.slice(this.matIndex, length).reduce((acc, mat) => acc + (mat.reveal && mat.reveal.leftInInches), 0)
      };
    },
    matImage () {
      if (this.fullMat && this.fullMat.matAsset) {
        return this.fullMat.matAsset;
      }
      return null;
    },
    matColor() {
      if (!this.fullMat) {
        return 'white';
      } else if (this.fullMat.surfaceTexture) {
        return `url(#${this.uuid}-mat-tile-pattern-${this.matIndex})`;
      } else {
        return this.fullMat.surfaceColor;
      }
    },
    surfaceTexture () {
      return this.fullMat ? this.fullMat.surfaceTexture : {};
    },
    surfaceTexturePatternOrigin () {
      if (this.surfaceTexture && this.surfaceTexture.alignment === "center") {
        return {
          x: (this.widthInInches - this.surfaceTexture.widthInInches) / 2,
          y: (this.heightInInches - this.surfaceTexture.heightInInches) / 2
        };
      } else {
        return {
          x: '0',
          y: '0'
        };
      }
    },
    coreColor() {
      return this.fullMat ? this.fullMat.coreColors[0] : 'black';
    },
    bevelWidth() {
      return defaultMatThickness; // 1/16th of an inch
    },
    widthInInches() {
      if (this.fullMat && this.fullMat.matWidth) {
        return `${this.fullMat.matWidth}`;
      }
      if (!this.artwork) return '0';

      return `${(this.totalsOfAllMatReveals.leftInInches + this.artwork.exterior.artworkOpeningWidthInInches + this.totalsOfAllMatReveals.rightInInches)}`;
    },
    heightInInches() {
      if (this.fullMat && this.fullMat.matHeight) {
        return `${this.fullMat.matHeight}`;
      }
      if (!this.artwork) return '0';

      return `${(this.totalsOfAllMatReveals.topInInches + this.artwork.exterior.artworkOpeningHeightInInches + this.totalsOfAllMatReveals.bottomInInches)}`;
    },
    matWidth() {
      if (this.fullMat && this.fullMat.matWidth) {
        return this.fullMat.matWidth;
      }
      if (!this.artwork) return 0;

      return this.totalsOfAllMatReveals.leftInInches + this.artwork.exterior.artworkOpeningWidthInInches + this.totalsOfAllMatReveals.rightInInches; // In inches
    },
    matHeight() {
      if (this.fullMat && this.fullMat.matHeight) {
        return this.fullMat.matHeight;
      }
      if (!this.artwork) return 0;

      return this.totalsOfAllMatReveals.topInInches + this.artwork.exterior.artworkOpeningHeightInInches + this.totalsOfAllMatReveals.bottomInInches; // In inches
    },
    firstArtworkOpeningX() {
      if (this.matImage) {
        return this.totalsOfAllMatRevealsThisMatAndAbove.leftInInches + (uploadedArtworkMatOverlap / 2);
      }
      return this.totalsOfAllMatRevealsThisMatAndAbove.leftInInches;
    },
    firstArtworkOpeningY() {
      if (this.matImage) {
        return this.totalsOfAllMatRevealsThisMatAndAbove.topInInches + (uploadedArtworkMatOverlap / 2);
      }
      return this.totalsOfAllMatRevealsThisMatAndAbove.topInInches;
    },
    artworkOpenings() {
      return this.frameSpec.artworks.map((artwork, artworkKey) => {
        const x = this.firstArtworkOpeningX + (this.fullMat && this.fullMat.openings ? this.fullMat.openings[artworkKey].leftInInches : 0);
        const y = this.firstArtworkOpeningY + (this.fullMat && this.fullMat.openings ? this.fullMat.openings[artworkKey].topInInches : 0);
        const width = !artwork ? 0
          : this.totalsOfAllMatRevealsBelowThisMat.leftInInches + artwork.exterior.artworkOpeningWidthInInches + this.totalsOfAllMatRevealsBelowThisMat.rightInInches;
        const height = !artwork ? 0
          :this.totalsOfAllMatRevealsBelowThisMat.topInInches + artwork.exterior.artworkOpeningHeightInInches + this.totalsOfAllMatRevealsBelowThisMat.bottomInInches;

        return {
          key: artworkKey,
          x,
          y,
          width,
          height,
          bevelX: x - (this.bevelWidth / 2),
          bevelY: y - (this.bevelWidth / 2),
          bevelWidth: width + this.bevelWidth,
          bevelHeight: height + this.bevelWidth,
        };
      });
    },
    matCaption() {
      return this.mat.matCaption || null;
    },
    captionX() {
      return this.firstArtworkOpeningX;
    },
    captionY() {
      return this.matHeight - this.totalsOfAllMatRevealsThisMatAndAbove.bottomInInches + matCaptionValues.offsetDown;
    },
    captionWidth() {
      return this.matWidth - this.totalsOfAllMatReveals.leftInInches - this.totalsOfAllMatReveals.rightInInches - (this.matImage ? uploadedArtworkMatOverlap : 0);
    },
    hasPlaque() {
      // todo: ensure this checks the actual value on the frameSpec
      return this.frameSpec.matPlaque || this.frameSpec.isAutoMatPlaque();
    },
    plaqueWidth() {
      // eventually we will have different plaque widths based on sku
      const smallPlaqueWidth = 3.75;
      return smallPlaqueWidth;
    },
    plaqueHeight() {
      // eventually we will have different plaque widths based on sku
      const smallPlaqueHeight = 2.25;
      return smallPlaqueHeight;
    },
    plaqueY() {
      // eventually we will have different distances for small vs large plaque, we only have small for now
      const smallPlaqueOffset = 0.75; // 0.75 inches below the artwork opening
      const largePlaqueOffset = 1; // 1 inch below the artwork opening
      return this.matHeight - this.totalsOfAllMatRevealsThisMatAndAbove.bottomInInches + smallPlaqueOffset;
    },
    plaqueX() {
      // from the left opening, subtract half of the opening, and half of the plaque width to center
      const baseMatOpeningWidth = this.captionWidth;
      return this.firstArtworkOpeningX + (baseMatOpeningWidth / 2) - (this.plaqueWidth / 2);
    }
  }
};
</script>

<style lang="scss" scoped>
svg {
  align-items: center;
  display: flex;
  height: auto;
  justify-content: center;
  max-width: 100%;
  pointer-events: none;
  width: 100%;

  rect {
    pointer-events: none;
  }
}
</style>